import { useState, useEffect } from "react";
import { z } from "zod";

import getAuthClient from "../auth-client.ts";
import { mapAwsError } from "../error-internal.ts";

// Deliberately not .passthrough() so we strip out the extra claims to be safe.
// updateAttributes() should only be called with the actually changed values
// regardless, but if it did get everything it would barf on all the OIDC claims
// like `iat`, and it also wouldn't be very happy with `cognito:username`!
const schema = z
  .object({
    email: z.string(),
    given_name: z.string(),
    family_name: z.string(),

    "custom:hasAcceptedPolicy": z.string().optional(),
    // ...
  })
  .nullable();

export interface CognitoAttributes {
  email: string;
  given_name: string;
  family_name: string;

  "custom:hasAcceptedPolicy"?: string;
}
const attributeNames: ReadonlyArray<keyof CognitoAttributes> = [
  "email",
  "given_name",
  "family_name",
  "custom:hasAcceptedPolicy",
];

export function getUserAttributes(): CognitoAttributes | null {
  return schema.parse(getAuthClient().loginClaims);
}

export function useUserAttributes(): CognitoAttributes | null {
  // Annoyingly, we do need to carefully cache the getUserAttributes update here.
  // The auth client currently updates loginClaims in place, which `useSyncExternalStore`
  // that we use elsewhere discards as "unchanged" if you use that directly.
  // You also can't just use the standard `useSyncExternalStore` with `getUserAttributes`
  // as the getter as that returns a new instance even without changes which react throws
  // a warning for, assuming you're failing to properly cache (which is sort of true).
  const [attributes, setAttributes] = useState(getUserAttributes);
  useEffect(() => {
    return getAuthClient().subscribe(() => {
      // Update instance if any attribute value has changed
      setAttributes((oldValues) => {
        const newValues = getUserAttributes();
        for (const name of attributeNames) {
          if (oldValues?.[name] !== newValues?.[name]) {
            return newValues;
          }
        }
        return oldValues;
      });
    });
  }, []);
  return attributes;
}

export async function refreshAttributes(): Promise<CognitoAttributes> {
  await getAuthClient().refreshLoginTokens().catch(mapAwsError);
  return getUserAttributes()!;
}

export async function updateAttributes(updates: Record<string, string | undefined>): Promise<readonly string[]> {
  const output = await getAuthClient().updateAttributes(updates).catch(mapAwsError);
  return output.requireConfirm;
}

export async function verifyAttributeUpdate(name: string, code: string): Promise<void> {
  await getAuthClient().confirmAttributeUpdate(name, code).catch(mapAwsError);
  // We don't want to track the pending values for attributes requiring confirmation.
  // await refreshAttributes();
}
